import { useState } from 'react';
import { Modal, Box, TextField, Button, Typography, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { addEmployee } from '../api/github';
import ScheduleSelector from './ScheduleSelector';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  width: '90%',
  maxWidth: 400,
  p: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const defaultSchedule = {
  workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
  restDays: ['sat', 'sun'],
  dailyHours: {
    start: '09:00',
    end: '18:00',
    breakStart: '13:00',
    breakEnd: '14:00'
  }
};

export default function AddEmployeeModal({ open, onClose, refreshList }) {
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    phones: [''],
    username: '',
    password: '',
    salary: 30000,
    schedule: defaultSchedule,
    other: ''
  });

  const [errors, setErrors] = useState({
    lastName: false,
    firstName: false,
    phones: [false],
    username: false,
    password: false,
    salary: false
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateForm = () => {
    const newErrors = {
      lastName: !formData.lastName.trim(),
      firstName: !formData.firstName.trim(),
      phones: formData.phones.map(phone => !phone.trim() || phone.replace(/\D/g, '').length < 12),
      username: !formData.username.trim(),
      password: !formData.password.trim(),
      salary: !formData.salary || isNaN(formData.salary) || Number(formData.salary) <= 0
    };
    setErrors(newErrors);
    return !Object.values(newErrors).flat().some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const fullName = `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();

      const employeeData = {
        ...formData,
        name: fullName,
        phones: formData.phones.map(phone => phone.replace(/\D/g, '')),
        other: formData.other || null
      };

      await addEmployee(employeeData);
      refreshList();
      onClose();
      setFormData({
        lastName: '',
        firstName: '',
        middleName: '',
        phones: [''],
        username: '',
        password: '',
        salary: 30000,
        schedule: defaultSchedule,
        other: ''
      });
    } catch (error) {
      alert('Хато: ' + error.message);
    }
  };

  const handlePhoneChange = (index, value) => {
    let formattedPhone = value.replace(/\D/g, '');
    if (formattedPhone.length > 0) {
      formattedPhone = '+' + formattedPhone;
      if (formattedPhone.length > 4) {
        formattedPhone = formattedPhone.substring(0, 4) + ' ' + formattedPhone.substring(4);
      }
      if (formattedPhone.length > 7) {
        formattedPhone = formattedPhone.substring(0, 7) + ' ' + formattedPhone.substring(7);
      }
      if (formattedPhone.length > 11) {
        formattedPhone = formattedPhone.substring(0, 11) + ' ' + formattedPhone.substring(11);
      }
      if (formattedPhone.length > 14) {
        formattedPhone = formattedPhone.substring(0, 14) + ' ' + formattedPhone.substring(14);
      }
      if (formattedPhone.length > 17) {
        formattedPhone = formattedPhone.substring(0, 17);
      }
    }
    const newPhones = [...formData.phones];
    newPhones[index] = formattedPhone;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhoneField = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
    setErrors({ ...errors, phones: [...errors.phones, false] });
  };

  const removePhoneField = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    const newPhoneErrors = errors.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones });
    setErrors({ ...errors, phones: newPhoneErrors });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6">Ходим қўшиш</Typography>
        <TextField
          label="Фамилия"
          fullWidth
          margin="normal"
          value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          required
          error={errors.lastName}
        />
        <TextField
          label="Имя"
          fullWidth
          margin="normal"
          error={errors.firstName}
          value={formData.firstName}
          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          required
        />
        <TextField
          label="Отчество"
          fullWidth
          margin="normal"
          value={formData.middleName}
          onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
        />
        {formData.phones.map((phone, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              label={`Телефон рақами ${index + 1}`}
              fullWidth
              margin="normal"
              value={phone}
              onChange={(e) => handlePhoneChange(index, e.target.value)}
              placeholder="+998 __ _______"
              error={errors.phones[index]}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[+][0-9]{3} [0-9]{2} [0-9]{3} [0-9]{10}'
              }}
            />
            {formData.phones.length > 1 && (
              <IconButton onClick={() => removePhoneField(index)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={addPhoneField}
          sx={{ mb: 2 }}
        >
          Номер қўшиш
        </Button>
        <TextField
          label="Қўшимча маълумотлар"
          fullWidth
          margin="normal"
          multiline
          minRows='4'
          value={formData.other}
          onChange={(e) => setFormData({ ...formData, other: e.target.value })}
          helperText="Қўшимча маълумотлар (ихтиёрий ходим +-лари)"
        />
        <TextField
          label="Логин"
          fullWidth
          margin="normal"
          error={errors.username}
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          required
        />
        <TextField
          label="Пароль"
          type="password"
          fullWidth
          margin="normal"
          error={errors.password}
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required
        />
        <TextField
          label="Иш хаққи"
          type="number"
          fullWidth
          margin="normal"
          value={formData.salary}
          onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
          error={errors.salary}
          required
        />
        <ScheduleSelector 
          schedule={formData.schedule} 
          onChange={(newSchedule) => 
            setFormData({ ...formData, schedule: newSchedule })
          } 
        />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          gap: 1, 
          mt: 1,
          alignItems: isMobile ? 'flex-start' : 'center'
        }}>
          <Button onClick={handleSubmit} fullWidth variant="contained">Ходимни қўшиш</Button>
          <Button onClick={onClose} fullWidth sx={{ mr: 0 }}>Бекор қилиш</Button>
        </Box>
      </Box>
    </Modal>
  );
}